import { Alert, Button, Chip, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserRoles } from '@bandido/shared/types';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { Link } from '@frontend/features/common/components/Link';
import { performanceError, usePerformanceAgency, usePerformanceAgencyMutation, usePerformanceCapture } from './queries';
import { PerformanceConfiguration } from './types';

export function AgencyPerformanceSetting({ agencyId }: { agencyId: number }) {
  const { myProfile } = useAuth();
  const { t } = useTranslation();
  const allowed = Boolean(myProfile?.roles?.split(',').includes(UserRoles.ADMINS));
  const query = usePerformanceAgency(myProfile?.id, agencyId, allowed);
  const mutation = usePerformanceAgencyMutation(agencyId);
  if (!allowed) return null;
  const known = query.data && !query.isError;
  return (
    <Stack spacing={2} maxWidth={700}>
      <Typography variant="h6">{t('workflowPerformance.agencyTitle')}</Typography>
      <Typography variant="body2">{t('workflowPerformance.agencyHint')}</Typography>
      {query.isError && <Alert severity="error">{t(performanceError(query.error))}</Alert>}
      {mutation.isError && <Alert severity="error">{t(performanceError(mutation.error))}</Alert>}
      {known && (
        <FormControlLabel
          label={t('workflowPerformance.agencyEnabled')}
          control={
            <Switch
              checked={query.data!.enabled}
              disabled={query.isFetching || mutation.isLoading}
              onChange={(_event, enabled) =>
                mutation.mutate(
                  { enabled },
                  {
                    onSettled: () => {
                      void query.refetch();
                    },
                  },
                )
              }
            />
          }
        />
      )}
      {query.isError && (
        <Button onClick={() => void query.refetch()} disabled={query.isFetching}>
          {t('workflowPerformance.refresh')}
        </Button>
      )}
      {!known && !query.isError && <Typography>{t('workflowPerformance.configLoading')}</Typography>}
    </Stack>
  );
}

export function CaptureControls({
  configuration,
  refreshing,
  onRefresh,
}: {
  configuration: PerformanceConfiguration;
  refreshing: boolean;
  onRefresh: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const mutation = usePerformanceCapture(configuration.agencyId, configuration.workflowId);
  const { capture, agencyEnabled } = configuration;
  const available = configuration.captureAvailable === true;
  const active = capture.state === 'requested' || capture.state === 'observing';
  const freshWindow = capture.state === 'idle';
  const startable = freshWindow || capture.state === 'paused' || capture.state === 'disabled';
  const busy = refreshing || mutation.isLoading;
  const act = (action: 'start' | 'pause') =>
    mutation.mutate(
      { action, revision: capture.revision },
      {
        onSettled: () => {
          void onRefresh();
        },
      },
    );
  return (
    <Stack spacing={2}>
      <Typography variant="h6">{t('workflowPerformance.captureTitle', { name: configuration.workflowName })}</Typography>
      <Alert severity={available ? 'info' : 'warning'}>
        {t(available ? 'workflowPerformance.processingReady' : 'workflowPerformance.processingUnavailable')}
      </Alert>
      <Chip
        sx={{ alignSelf: 'flex-start' }}
        color={available && capture.state === 'observing' ? 'success' : 'default'}
        label={t(`workflowPerformance.capture_${capture.state}`)}
      />
      <Typography variant="body2">
        {t('workflowPerformance.captureHint', { seconds: capture.propagationSeconds, limit: capture.limit })}
      </Typography>
      {!agencyEnabled && (
        <Alert severity="info">
          {t('workflowPerformance.enableAgencyHint')}{' '}
          <Link to={`/dashboard/agencies/${configuration.agencyId}/settings`}>
            {t('workflowPerformance.agencySettings')}
          </Link>
        </Alert>
      )}
      {capture.state === 'busy' && <Alert severity="info">{t('workflowPerformance.reservedHint')}</Alert>}
      {capture.state === 'limit' && <Alert severity="warning">{t('workflowPerformance.limitHint')}</Alert>}
      {capture.startedAt && (
        <Typography variant="body2">
          {t('workflowPerformance.captureStarted', { at: new Date(capture.startedAt).toLocaleString() })}
        </Typography>
      )}
      {capture.startedAt && (
        <Typography variant="body2">
          {t('workflowPerformance.captureTotal', { count: capture.accepted })}
        </Typography>
      )}
      {capture.heartbeatAt && (
        <Typography variant="body2">
          {t('workflowPerformance.captureHeartbeat', { at: new Date(capture.heartbeatAt).toLocaleString() })}
        </Typography>
      )}
      {mutation.isError && <Alert severity="error">{t(performanceError(mutation.error))}</Alert>}
      <Stack direction="row" spacing={2}>
        {startable && (
          <Button variant="contained" disabled={busy || !agencyEnabled || !available} onClick={() => act('start')}>
            {t(freshWindow ? 'workflowPerformance.startCapture' : 'workflowPerformance.resumeCapture')}
          </Button>
        )}
        {active && (
          <Button variant="outlined" disabled={busy} onClick={() => act('pause')}>
            {t('workflowPerformance.pauseCapture')}
          </Button>
        )}
        <Button disabled={busy} onClick={() => void onRefresh()}>
          {t('workflowPerformance.refresh')}
        </Button>
      </Stack>
    </Stack>
  );
}
